import { InvitationResponseDto, InvitedGuestResponseDto, } from '@lucidlink/dto/web-service-api';
import { base64ToArray, cryptoPairsFromArray, decryptWithRsaBlockCipher, verifyPublicPrivateKeyPair, } from '@lucidlink/ts-crypto/crypto';
import { NotFoundError } from '#fetcher/fetch-error.types';
import { arrayParserFromDto, parserFromDto } from '#fetcher/parse-utils';
import { prepareIndividualInvitation, prepareInvitedMember } from '#sdk/common';
import { prepareGuestInvitation, prepareOrganizationInvitation, } from '#sdk/common/invitation';
import { prepareSsoInvitation } from '#sdk/common/invitation/prepare-sso-invitation.ts';
import { TimeCachedValue } from '#sdk/utils/time-cached-value';
import { generateInvitation } from './generate-invitation';
import { remapInvitationResponse } from './remap-invitation-response';
export const createInvitationManager = ({ workspaceId, workspaceFetcher, membersMap, loginToWorkspace, getAdminCrypto, getPrincipalsCrypto, }) => {
    return new InvitationManagementImpl(workspaceId, membersMap, loginToWorkspace, getAdminCrypto, getPrincipalsCrypto, workspaceFetcher);
};
class InvitationManagementImpl {
    workspaceId;
    membersMap;
    loginToWorkspace;
    getAdminCrypto;
    getPrincipalsCrypto;
    individualInvitationFetcher;
    organizationInvitationFetcher;
    ssoInvitationFetcher;
    guestInvitationFetcher;
    constructor(workspaceId, membersMap, loginToWorkspace, getAdminCrypto, getPrincipalsCrypto, workspaceFetcher) {
        this.workspaceId = workspaceId;
        this.membersMap = membersMap;
        this.loginToWorkspace = loginToWorkspace;
        this.getAdminCrypto = getAdminCrypto;
        this.getPrincipalsCrypto = getPrincipalsCrypto;
        this.individualInvitationFetcher = workspaceFetcher.createSubFetcher({
            path: ['invitations', 'individual'],
        });
        this.organizationInvitationFetcher = workspaceFetcher.createSubFetcher({
            path: ['invitations', 'organization'],
        });
        this.ssoInvitationFetcher = workspaceFetcher.createSubFetcher({
            path: ['invitations', 'sso'],
        });
        this.guestInvitationFetcher = workspaceFetcher.createSubFetcher({
            path: ['invitations', 'guest'],
        });
    }
    async getGuestInvitation() {
        const adminCrypto = await this.getAdminCrypto();
        const guestInvitation = await this.guestInvitation.get();
        return remapInvitationResponse(guestInvitation.data, adminCrypto);
    }
    async createIndividualInvitation(emails, groupIds = []) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const principalsCrypto = await this.getPrincipalsCrypto();
        const individualInvitation = await prepareIndividualInvitation({
            emails,
            adminCrypto,
            groupIds,
            principalsCrypto,
            workspaceId: this.workspaceId,
        });
        await this.individualInvitationFetcher.post([], {
            body: individualInvitation.invitation,
        });
        (await this.getPrincipalsCrypto()).refresh();
        await this.membersMap.refresh();
        return generateInvitation({
            invitationId: individualInvitation.invitation.id,
            linkSecret: individualInvitation.invitationSecret,
        });
    }
    async createOrganizationInvitation() {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const organizationInvitation = await prepareOrganizationInvitation({
            workspaceId: this.workspaceId,
            adminCrypto,
        });
        await this.organizationInvitationFetcher.post([], {
            body: organizationInvitation.invitation,
        });
        return generateInvitation({
            invitationId: organizationInvitation.invitation.id,
            linkSecret: organizationInvitation.invitationSecret,
        });
    }
    async revokeOrganizationInvitation() {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        await this.organizationInvitationFetcher.delete([], {});
    }
    async getOrganizationInvitation() {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const response = await this.organizationInvitationFetcher.get([], {
            parser: parserFromDto(InvitationResponseDto),
        });
        return remapInvitationResponse(response, adminCrypto);
    }
    async inviteGuests(emails) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const principalsCrypto = await this.getPrincipalsCrypto();
        const { data: guestInvitation, keyPairs: invitationKeys } = await this.guestInvitation.get();
        const members = await Promise.all(emails.map(async (email) => {
            const { groupMemberships, ...member } = await prepareInvitedMember({
                workspaceId: this.workspaceId,
                invitationKeys,
                email,
                principalsCrypto,
                groupIds: [],
            });
            return member;
        }));
        const guestsData = await this.guestInvitationFetcher.post(['members'], {
            body: { members },
            parser: arrayParserFromDto(InvitedGuestResponseDto),
        });
        // if we are going to ensure new members and give them permissions right after that
        // we need to refresh our principals crypto
        (await this.getPrincipalsCrypto()).refresh();
        await this.membersMap.refresh();
        return {
            invitationData: await remapInvitationResponse(guestInvitation, adminCrypto),
            guestsData,
        };
    }
    async ensureSsoInvitation() {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const { invitation } = await prepareSsoInvitation({
            workspaceId: this.workspaceId,
            adminCrypto,
        });
        await this.ssoInvitationFetcher.post([], {
            body: invitation,
        });
        return this.getSsoInvitation();
    }
    async getSsoInvitation() {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const response = await this.ssoInvitationFetcher.get([], {
            parser: parserFromDto(InvitationResponseDto),
        });
        return remapInvitationResponse(response, adminCrypto);
    }
    guestInvitation = new TimeCachedValue(async () => {
        const adminCrypto = await this.getAdminCrypto();
        let guestInvitation;
        try {
            guestInvitation = await this.guestInvitationFetcher.get([], {
                parser: parserFromDto(InvitationResponseDto),
            });
        }
        catch (error) {
            if (error instanceof NotFoundError) {
                await this.ensureGuestInvitation(adminCrypto);
                guestInvitation = await this.guestInvitationFetcher.get([], {
                    parser: parserFromDto(InvitationResponseDto),
                });
            }
            else {
                throw error;
            }
        }
        const adminKeys = await adminCrypto.verifyAndGetAdminKeys();
        const invitationPrivateKey = await decryptWithRsaBlockCipher(guestInvitation.ePrivateKeyByAdminKey, adminKeys.encryptPair.privateKey);
        const invitationPublicKey = base64ToArray(guestInvitation.publicKey);
        const keyPairs = await cryptoPairsFromArray(invitationPublicKey, invitationPrivateKey);
        await verifyPublicPrivateKeyPair(keyPairs.encryptPair);
        return { data: guestInvitation, keyPairs };
    }, {
        expirationTime: 'neverExpire',
    });
    async ensureGuestInvitation(adminCrypto) {
        const guestParams = {
            adminKeys: await adminCrypto.verifyAndGetAdminKeys(),
            workspaceCommonKey: await adminCrypto.getWorkspaceCommonKey(),
            everyonePrivateKey: (await adminCrypto.verifyAndGetEveryoneKeys())
                .privateKey,
            workspaceId: this.workspaceId,
            workspaceSecretKey: await adminCrypto
                .getSsoCrypto()
                .then((ssoCrypto) => ssoCrypto.verifyAndGetSsoSecretKey()),
        };
        const guestInvitation = await prepareGuestInvitation(guestParams);
        await this.guestInvitationFetcher.post([], {
            body: guestInvitation,
        });
    }
}
export * from './generate-invitation';
